"use client"

import { useState, useEffect, Fragment } from "react"
import { Dialog, Transition } from "@headlessui/react"
import { X, Save, RotateCw, AlertCircle, CheckCircle, Clock, Edit, Lock, ArrowRight } from "lucide-react"
import ticketsService from "../../services/ticketsService"
import toast from "react-hot-toast"

export default function UpdateTicketStatusModal({ isOpen, onClose, ticket, onSuccess }) {
  const [status, setStatus] = useState("")
  const [resolutionNotes, setResolutionNotes] = useState("")
  const [processing, setProcessing] = useState(false)
  const [showConfirmation, setShowConfirmation] = useState(false)
  const [errors, setErrors] = useState({
    status: "",
    resolutionNotes: "",
  })

  // Reset form when modal opens/closes or ticket changes
  useEffect(() => {
    if (isOpen && ticket) {
      setStatus(ticket.status || "")
      setResolutionNotes("")
      setShowConfirmation(false)
      setErrors({
        status: "",
        resolutionNotes: "",
      })
    } else {
      setStatus("")
      setResolutionNotes("")
      setShowConfirmation(false)
      setErrors({
        status: "",
        resolutionNotes: "",
      })
    }
  }, [isOpen, ticket])

  const getStatusIcon = (statusValue) => {
    switch (statusValue) {
      case "open":
        return <AlertCircle size={18} className="text-blue-600 dark:text-blue-400" />
      case "in_progress":
        return <Clock size={18} className="text-yellow-600 dark:text-yellow-400" />
      case "resolved":
        return <CheckCircle size={18} className="text-green-600 dark:text-green-400" />
      case "closed":
        return <Lock size={18} className="text-gray-600 dark:text-gray-400" />
      default:
        return <AlertCircle size={18} className="text-gray-600 dark:text-gray-400" />
    }
  }

  const getStatusColor = (statusValue) => {
    switch (statusValue) {
      case "open":
        return "bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-300 border-blue-200 dark:border-blue-800"
      case "in_progress":
        return "bg-yellow-50 text-yellow-700 dark:bg-yellow-900/20 dark:text-yellow-300 border-yellow-200 dark:border-yellow-800"
      case "resolved":
        return "bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-300 border-green-200 dark:border-green-800"
      case "closed":
        return "bg-gray-50 text-gray-700 dark:bg-gray-900/20 dark:text-gray-300 border-gray-200 dark:border-gray-800"
      default:
        return "bg-gray-50 text-gray-700 dark:bg-gray-900/20 dark:text-gray-300 border-gray-200 dark:border-gray-800"
    }
  }

  // Check if status transition is allowed
  const isStatusTransitionAllowed = (fromStatus, toStatus) => {
    // Once resolved or closed, cannot go back to open/in_progress
    if ((fromStatus === "resolved" || fromStatus === "closed") && (toStatus === "open" || toStatus === "in_progress")) {
      return false
    }
    return true
  }

  const getAvailableStatusOptions = () => {
    const allOptions = [
      {
        value: "open",
        label: "Open",
        description: "Ticket is newly created and awaiting attention",
        emoji: "",
        gradient: "from-blue-500 to-blue-600",
      },
      {
        value: "in_progress",
        label: "In Progress",
        description: "Ticket is being actively worked on",
        emoji: "",
        gradient: "from-yellow-500 to-yellow-600",
      },
      {
        value: "resolved",
        label: "Resolved",
        description: "Issue has been resolved and conversations will be closed",
        emoji: "",
        gradient: "from-green-500 to-green-600",
      },
      {
        value: "closed",
        label: "Closed",
        description: "Ticket is closed and no further action or conversations allowed",
        emoji: "",
        gradient: "from-gray-500 to-gray-600",
      },
    ]

    // Filter options based on current status
    return allOptions.filter((option) => isStatusTransitionAllowed(ticket?.status, option.value))
  }

  // Validate form
  const validateForm = () => {
    const newErrors = {
      status: "",
      resolutionNotes: "",
    }

    let isValid = true

    if (!status) {
      newErrors.status = "Please select a status"
      isValid = false
    }

    // Require resolution notes for resolved/closed status
    if ((status === "resolved" || status === "closed") && !resolutionNotes.trim()) {
      newErrors.resolutionNotes = "Resolution notes are required when marking ticket as resolved or closed"
      isValid = false
    }

    setErrors(newErrors)
    return isValid
  }

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    // Show confirmation for closing actions
    if ((status === "resolved" || status === "closed") && !showConfirmation) {
      setShowConfirmation(true)
      return
    }

    try {
      setProcessing(true)

      const payload = {
        status,
        ...(resolutionNotes.trim() && { resolution_notes: resolutionNotes.trim() }),
      }

      const response = await ticketsService.updateStatus(ticket._id, payload)

      if (response.data && response.data.statusCode === 200) {
        const statusMessage =
          status === "resolved"
            ? "Ticket resolved and conversations closed"
            : status === "closed"
              ? "Ticket closed and conversations disabled"
              : "Ticket status updated successfully"

        toast.success(statusMessage)

        if (onSuccess) {
          onSuccess()
        }
        onClose()
      } else {
        throw new Error(response.data.message || "Failed to update ticket status")
      }
    } catch (error) {
      console.error("Error updating ticket status:", error)
      toast.error(error.response?.data?.message || error.message || "Failed to update ticket status")
    } finally {
      setProcessing(false)
    }
  }

  // Handle input changes
  const handleChange = (field, value) => {
    if (field === "status") {
      setStatus(value)
      setShowConfirmation(false) // Reset confirmation when status changes
    } else if (field === "resolutionNotes") {
      setResolutionNotes(value)
    }

    // Clear error when field is updated
    if (errors[field]) {
      setErrors({ ...errors, [field]: "" })
    }
  }

  const availableOptions = getAvailableStatusOptions()

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-3xl transform overflow-hidden rounded-2xl bg-white dark:bg-neutral-950 shadow-2xl transition-all">
                {/* Enhanced Header with Gradient */}
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-neutral-800 dark:to-neutral-800 border-b border-neutral-200 dark:bg-neutral-800 p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-white dark:bg-neutral-700 rounded-xl shadow-sm">
                        <Edit className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                      </div>
                      <div>
                        <Dialog.Title className="text-xl font-bold text-neutral-900 dark:text-white">
                          Update Ticket Status
                        </Dialog.Title>
                        {ticket && (
                          <p className="text-sm text-neutral-600 dark:text-neutral-400 mt-1">
                            {ticket.ticket_id} - {ticket.title}
                          </p>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={onClose}
                      className="p-2 rounded-xl text-neutral-500 hover:bg-white/50 dark:text-neutral-400 dark:hover:bg-neutral-700 transition-all duration-200"
                      type="button"
                      aria-label="Close"
                    >
                      <X size={20} />
                    </button>
                  </div>
                </div>

                {/* Content */}
                <div className="p-6 space-y-8">
                  {/* Current Status Display */}
                  {ticket && (
                    <div className={`p-5 rounded-xl border-2 ${getStatusColor(ticket.status)}`}>
                      <div className="flex items-center gap-4 mb-3">
                        <div className="p-2 bg-white dark:border-neutral-900 dark:bg-neutral-900/30 rounded-lg shadow-sm">
                          {getStatusIcon(ticket.status)}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-3">
                            <span className="text-sm font-semibold text-neutral-900 dark:text-white">
                              Current Status:
                            </span>
                            <span className="px-3 py-1.5 rounded-full text-sm font-medium bg-white/80 dark:border-neutral-900 dark:bg-neutral-900/80 text-neutral-900 dark:text-white">
                              {ticket.status.replace("_", " ").replace(/\b\w/g, (l) => l.toUpperCase())}
                            </span>
                          </div>
                          <p className="text-xs text-neutral-600 dark:text-neutral-400 mt-1">
                            Last updated:{" "}
                            {new Date(ticket.updatedAt).toLocaleDateString("en-GB", {
                              day: "2-digit",
                              month: "short",
                              year: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Status Selection */}
                  <div className="space-y-4">
                    <div className="flex items-center gap-2">
                      <label className="text-sm font-medium text-neutral-900 dark:text-white">Select New Status</label>
                      <span className="text-red-500">*</span>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {availableOptions.map((option) => (
                        <button
                          key={option.value}
                          type="button"
                          onClick={() => handleChange("status", option.value)}
                          className={`group text-left flex items-start gap-4 p-5 border-2 rounded-xl transition-all duration-200 hover:shadow-lg ${status === option.value
                            ? "border-blue-500 bg-blue-50 dark:bg-blue-900/20 ring-2 ring-blue-500/20 shadow-lg"
                            : "border-neutral-200 dark:bg-neutral-800 hover:border-neutral-300 dark:hover:border-neutral-600 hover:bg-neutral-50 dark:hover:bg-neutral-800/50"
                            }`}
                        >
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-3 mb-2">
                              <span>{option.emoji}</span>
                              <div className="p-1.5 bg-white dark:border-neutral-900 dark:bg-neutral-900/30 rounded-lg shadow-sm group-hover:shadow-md transition-shadow">
                                {getStatusIcon(option.value)}
                              </div>
                              <span className="text-sm font-medium text-neutral-900 dark:text-white">
                                {option.label}
                              </span>
                            </div>
                            <p className="text-xs text-neutral-600 dark:text-neutral-400 leading-relaxed">
                              {option.description}
                            </p>
                          </div>
                          {status === option.value && (
                            <div className="absolute top-3 right-3">
                              <div className="w-3 h-3 bg-blue-500 rounded-full animate-pulse"></div>
                            </div>
                          )}
                        </button>
                      ))}
                    </div>
                    {errors.status && (
                      <p className="text-sm text-red-500 flex items-center gap-2">
                        <AlertCircle size={16} />
                        {errors.status}
                      </p>
                    )}
                  </div>

                  {/* Resolution Notes */}
                  <div className="space-y-3">
                    <label className="flex items-center gap-2 text-sm font-medium text-neutral-900 dark:text-white">
                      Resolution Notes
                      {(status === "resolved" || status === "closed") && (
                        <span className="text-red-500 text-lg">*</span>
                      )}
                    </label>
                    <div className="relative">
                      <textarea
                        value={resolutionNotes}
                        onChange={(e) => handleChange("resolutionNotes", e.target.value)}
                        placeholder={
                          status === "resolved" || status === "closed"
                            ? "Please provide details about how the issue was resolved..."
                            : "Optional notes about the status change..."
                        }
                        rows={4}
                        className={`w-full text-neutral-800 dark:text-neutral-200 px-4 py-4 text-sm bg-white dark:border-neutral-900 dark:bg-neutral-900/30 border-2 border-neutral-200 dark:bg-neutral-800 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-200 resize-none ${errors.resolutionNotes ? "border-red-500 focus:border-red-500 focus:ring-red-500/20" : ""
                          }`}
                      />
                    </div>
                    <p className="text-xs text-neutral-500 dark:text-neutral-400">
                      {status === "resolved" || status === "closed"
                        ? "Required when marking ticket as resolved or closed"
                        : "Optional additional information about the status change"}
                    </p>
                    {errors.resolutionNotes && (
                      <p className="text-sm text-red-500 flex items-center gap-2">
                        <AlertCircle size={16} />
                        {errors.resolutionNotes}
                      </p>
                    )}
                  </div>

                  {/* Confirmation Step */}
                  {showConfirmation && (status === "resolved" || status === "closed") && (
                    <div className="p-5 bg-amber-50 dark:bg-amber-900/20 rounded-xl border-2 border-amber-200 dark:border-amber-800">
                      <div className="flex items-start gap-3">
                        <AlertCircle size={20} className="text-amber-600 dark:text-amber-400 mt-0.5" />
                        <div>
                          <h3 className="text-sm font-medium text-amber-800 dark:text-amber-300 mb-2">
                            Confirm Ticket Closure
                          </h3>
                          <p className="text-sm text-amber-700 dark:text-amber-400 mb-3">
                            You are about to {status} this ticket. This action will:
                          </p>
                          <ul className="text-sm text-amber-700 dark:text-amber-400 space-y-1 ml-4">
                            <li>• Close all conversations permanently</li>
                            <li>• Prevent further messages from being sent</li>
                            <li>• Notify the client of the status change</li>
                            <li>• Add a system message to the timeline</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Information box */}
                  <div className="p-5 bg-blue-50 dark:bg-blue-900/20 rounded-xl border border-blue-200 dark:border-blue-800">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="p-2 bg-blue-100 dark:bg-blue-900/40 rounded-lg">
                        <AlertCircle size={18} className="text-blue-600 dark:text-blue-400" />
                      </div>
                      <h3 className="text-sm font-medium text-blue-800 dark:text-blue-300">
                        Status Update Information
                      </h3>
                    </div>
                    <ul className="text-sm text-blue-700 dark:text-blue-300 space-y-2">
                      <li className="flex items-center gap-2">
                        <ArrowRight size={14} />
                        The client will be notified of this status change
                      </li>
                      <li className="flex items-center gap-2">
                        <ArrowRight size={14} />A system message will be added to the ticket timeline
                      </li>
                      <li className="flex items-center gap-2">
                        <ArrowRight size={14} />
                        Resolution notes will be visible to all participants
                      </li>
                      {(status === "resolved" || status === "closed") && (
                        <li className="flex items-center gap-2 font-semibold">
                          <Lock size={14} />
                          Conversations will be closed and no further messages can be sent
                        </li>
                      )}
                    </ul>
                  </div>
                </div>

                {/* Enhanced Footer */}
                <div className="border-t border-neutral-200 dark:border-neutral-900 dark:bg-neutral-900/30 p-6 bg-neutral-50 dark:border-neutral-900 dark:bg-neutral-900/50">
                  <div className="flex justify-end gap-3">
                    <button
                      type="button"
                      onClick={onClose}
                      className="px-6 py-3 text-sm font-medium text-neutral-700 bg-white border-2 border-neutral-300 rounded-xl hover:bg-neutral-50 dark:border-neutral-900 dark:bg-neutral-900/30 dark:text-neutral-300 dark:border-neutral-600 dark:hover:bg-neutral-700 focus:outline-none focus:ring-2 focus:ring-neutral-500/20 transition-all duration-200"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleSubmit}
                      disabled={processing || !status}
                      className="inline-flex items-center px-6 py-3 text-sm font-semibold text-white bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl hover:from-blue-700 hover:to-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500/20 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg hover:shadow-xl"
                    >
                      {processing ? (
                        <>
                          <RotateCw className="mr-2 h-4 w-4 animate-spin" />
                          Updating...
                        </>
                      ) : showConfirmation ? (
                        <>
                          <CheckCircle className="mr-2 h-4 w-4" />
                          Confirm & {status === "resolved" ? "Resolve" : "Close"} Ticket
                        </>
                      ) : (
                        <>
                          <Save className="mr-2 h-4 w-4" />
                          {status === "resolved" || status === "closed" ? "Close Ticket" : "Update Status"}
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  )
}
